import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Target, Info } from 'lucide-react';

const ratingClass = (rating) => {
  if (rating === 'Strong Buy' || rating === 'Buy') return 'bg-green-600 text-white';
  if (rating === 'Hold') return 'bg-yellow-500 text-white';
  return 'bg-red-600 text-white';
};

const sentimentClass = (sentiment) => {
  if (sentiment === 'bullish') return 'border-green-500 text-green-600';
  if (sentiment === 'bearish') return 'border-red-500 text-red-600';
  return 'border-gray-400';
};

const ConsensusBar = ({ consensus }) => {
  const total = consensus.total || 1;
  return (
    <div className="space-y-2 mb-4">
      <div className="flex justify-between text-sm">
        <span className="text-green-600">
          Buy: {consensus.buy}
          {consensus.strongBuy > 0 && <span className="text-xs ml-1">({consensus.strongBuy} strong)</span>}
        </span>
        <span className="text-yellow-600">Hold: {consensus.hold}</span>
        <span className="text-red-600">
          Sell: {consensus.sell}
          {consensus.strongSell > 0 && <span className="text-xs ml-1">({consensus.strongSell} strong)</span>}
        </span>
      </div>
      <div className="flex h-3 rounded-full overflow-hidden">
        <div className="bg-green-600" style={{ width: `${(consensus.buy / total) * 100}%` }} />
        <div className="bg-yellow-500" style={{ width: `${(consensus.hold / total) * 100}%` }} />
        <div className="bg-red-600" style={{ width: `${(consensus.sell / total) * 100}%` }} />
      </div>
      {consensus.total > 0 && (
        <p className="text-xs text-gray-500 text-center">Based on {consensus.total} analyst ratings</p>
      )}
    </div>
  );
};

const RecentActions = ({ actions }) => {
  if (!actions?.length) return null;
  return (
    <div className="mb-4">
      <p className="font-semibold text-gray-900 text-sm mb-2">Recent Actions</p>
      <div className="space-y-2">
        {actions.slice(0, 3).map((action, index) => (
          <div
            key={`${action.firm}-${action.date || index}`}
            className="flex justify-between items-center text-xs bg-gray-50 p-2 rounded"
          >
            <div>
              <span className="font-medium">{action.firm}</span>
              <span className="text-gray-500 ml-1">({action.firmType})</span>
            </div>
            <div className="text-right">
              <Badge variant="outline" className={`text-xs ${sentimentClass(action.sentiment)}`}>
                {action.action}: {action.toRating}
              </Badge>
              {action.date && <p className="text-gray-400 mt-1">{action.date}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const AnalystList = ({ analysts }) => (
  <div className="space-y-3 mb-4">
    <p className="font-semibold text-gray-900 text-sm">Analyst Ratings</p>
    {analysts.slice(0, 5).map((analyst, index) => (
      <div key={`${analyst.firm}-${index}`} className="flex justify-between items-center text-sm">
        <div>
          <span className="text-gray-700">{analyst.firm}</span>
          {analyst.firmType && <span className="text-xs text-gray-400 ml-1">• {analyst.firmType}</span>}
        </div>
        <div className="text-right">
          <Badge variant="outline" className={`text-xs ${sentimentClass(analyst.sentiment)}`}>
            {analyst.rating}
          </Badge>
          <p className="text-gray-600 mt-1">${analyst.target}</p>
        </div>
      </div>
    ))}
  </div>
);

const DataSources = ({ sources }) => (
  <div className="flex items-start gap-2 border-t border-gray-100 pt-3 mt-1 text-xs text-gray-500">
    <Info size={14} className="mt-0.5 text-gray-400 flex-shrink-0" />
    <div>
      <p className="font-medium mb-1">Data Sources:</p>
      {sources && sources.length > 0 ? (
        <ul className="list-disc list-inside">
          {sources.map((source) => <li key={source}>{source}</li>)}
        </ul>
      ) : (
        <p>Yahoo Finance (aggregated from multiple providers)</p>
      )}
      <p className="mt-1 italic">Always verify with your broker before making trading decisions.</p>
    </div>
  </div>
);

const AnalystConsensusCard = ({ analystRatings, loading }) => (
  <Card data-testid="analyst-consensus-card">
    <CardHeader>
      <CardTitle className="text-lg">Analyst Consensus</CardTitle>
    </CardHeader>
    <CardContent>
      {loading ? (
        <p className="text-center text-gray-500">Loading analyst data...</p>
      ) : analystRatings ? (
        <>
          <div className="text-center mb-6">
            <Badge className={`text-lg px-4 py-2 mb-4 ${ratingClass(analystRatings.rating)}`}>
              {analystRatings.rating}
            </Badge>
            {analystRatings.ratingScore && (
              <p className="text-xs text-gray-500 mb-2">Score: {analystRatings.ratingScore}/5.0</p>
            )}
            <div className="flex items-center justify-center gap-2 text-gray-600">
              <Target size={20} />
              <div>
                <p className="text-sm">Price Target</p>
                <p className="text-2xl font-bold text-gray-900">${analystRatings.targetPrice}</p>
                {analystRatings.upside !== undefined && (
                  <p className={`text-sm ${analystRatings.upside >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {analystRatings.upside >= 0 ? '+' : ''}{analystRatings.upside}% upside
                  </p>
                )}
              </div>
            </div>
            {analystRatings.targetHigh && analystRatings.targetLow && (
              <p className="text-xs text-gray-500 mt-2">
                Range: ${analystRatings.targetLow} - ${analystRatings.targetHigh}
              </p>
            )}
          </div>

          <ConsensusBar consensus={analystRatings.consensus} />
          <RecentActions actions={analystRatings.recentActions} />
          <AnalystList analysts={analystRatings.analysts || []} />

          {analystRatings.institutionalHoldings && (
            <div className="flex justify-between items-center text-sm mb-4 p-2 bg-purple-50 rounded">
              <span className="text-gray-700">Institutional Ownership</span>
              <span className="font-semibold text-purple-600">{analystRatings.institutionalHoldings}%</span>
            </div>
          )}

          <DataSources sources={analystRatings.dataSources} />
        </>
      ) : (
        <p className="text-center text-gray-500">No analyst data available</p>
      )}
    </CardContent>
  </Card>
);

export default AnalystConsensusCard;
